/*!
 * Copyright © 2022-2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { interval, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { isElementNull } from '../../../../utils';
import {
  EDeviceEventTypeIndicator,
  EHealthCheckRequestState,
  IDeviceHealthCheckDeviceEventResponse
} from '../../../device-health-check/models';
import { ROUTE_PARAMS } from '../../constants';
import { EPlotType, IVehicleWithBoxResponse, IVehicleWithEsnResponse } from '../../models';
import { delayedRetry } from '../../operators';
import { DeviceHealthCheckHttpService } from '../../services';
import {
  EDeviceHealthCheckActions,
  GetHistory,
  GetHistorySuccess,
  GetSelectedStatusItemId,
  GetVehicleSpecification,
  GetVehicleSpecificationSuccess,
  GetVehicleWithEsn,
  GetVehicleWithEsnSuccess,
  OpenTroubleshootingPanel,
  PingResponseFailure,
  PingResponsePending,
  PingResponseSuccess,
  ResetResponseFailure,
  ResetResponsePending,
  ResetResponseSuccess,
  SelectRoutedVehicle,
  SendPing,
  SendPingFailure,
  SendPingSuccess,
  SendReset,
  SendResetFailure,
  SendResetSuccess,
  SetSidePanelSelectedItem,
  StopDeviceHealthCheckPolling
} from '../actions';
import { SelectVehicleIdForDeviceHealth } from '../actions/device-health.actions';
import { getIsSidePanelOpen, getPingRequestCorrelationId, getVehicleWithBoxResponse, getVehicleWithEsnResponse } from '../selectors';
import { IAppState } from '../state';

@Injectable()
export class DeviceHealthCheckEffects {
  private readonly _pollingIntervalMs = 5000;

  public getVehicleSpecification$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVehicleSpecification>(EDeviceHealthCheckActions.GetVehicleSpecification),
      map(action => action.payload),
      switchMap((vehicleId: number) =>
        this._deviceHealthCheckHttpService.getVehicleSpecification(vehicleId).pipe(
          delayedRetry(),
          catchError(() => of(<IVehicleWithBoxResponse>null))
        )
      ),
      filter(vehicleWithBoxResponse => !isElementNull(vehicleWithBoxResponse)),
      map(vehicleWithBoxResponse => new GetVehicleSpecificationSuccess(vehicleWithBoxResponse))
    )
  );

  public getVehicleWithEsn$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVehicleWithEsn>(EDeviceHealthCheckActions.GetVehicleWithEsn),
      map(action => action.payload),
      switchMap((vehicleId: number) =>
        this._deviceHealthCheckHttpService.getVehicleWithEsn(vehicleId).pipe(
          delayedRetry(),
          catchError(() => of(<IVehicleWithEsnResponse>null))
        )
      ),
      filter(vehicleWithEsnResponse => !isElementNull(vehicleWithEsnResponse)),
      map(vehicleWithEsnResponse => new GetVehicleWithEsnSuccess(vehicleWithEsnResponse))
    )
  );

  public setSidePanelSelectedItem$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSidePanelSelectedItem>(EDeviceHealthCheckActions.SetSidePanelSelectedItem),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getIsSidePanelOpen))),
      switchMap(([selectedScrollItem, isSidePanelOpen]) => {
        if (isSidePanelOpen) {
          return selectedScrollItem.type === EPlotType.asset
            ? [new GetSelectedStatusItemId(selectedScrollItem.id)]
            : [
                new GetSelectedStatusItemId(selectedScrollItem.id),
                new GetVehicleSpecification(selectedScrollItem.id),
                new GetVehicleWithEsn(selectedScrollItem.id)
              ];
        } else {
          return [];
        }
      })
    )
  );

  public sendPing$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SendPing>(EDeviceHealthCheckActions.SendPing),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getVehicleWithBoxResponse))),
      switchMap(([esn, vehicleWithBoxResponse]) => this._deviceHealthCheckHttpService.sendPing(esn, vehicleWithBoxResponse.boxId)),
      map(response => (response?.transactionId ? new SendPingSuccess(response) : new SendPingFailure()))
    )
  );

  public sendPingSuccessStartPollingResponse$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SendPingSuccess>(EDeviceHealthCheckActions.SendPingSuccess),
      switchMap(() =>
        interval(this._pollingIntervalMs).pipe(
          takeUntil(
            this._actions$.pipe(
              ofType<PingResponseSuccess | PingResponseFailure | StopDeviceHealthCheckPolling>(
                EDeviceHealthCheckActions.PingResponseSuccess,
                EDeviceHealthCheckActions.PingResponseFailure,
                EDeviceHealthCheckActions.StopDeviceHealthCheckPolling
              )
            )
          )
        )
      ),
      withLatestFrom(this._store.pipe(select(getVehicleWithEsnResponse))),
      switchMap(([, vehicleWithEsnResponse]) =>
        this._deviceHealthCheckHttpService.getDeviceHealthCheckStatus(0, 1, vehicleWithEsnResponse.esn)
      ),
      map(response => {
        if (response.results.length > 0) {
          for (let i = 0; i <= response.results[0].length; i++) {
            const eventType = response.results[0][i]?.eventTypeInd;
            if (eventType === EDeviceEventTypeIndicator.PingFailed) {
              return new PingResponseFailure();
            }
            if (eventType === EDeviceEventTypeIndicator.PingSuccess) {
              return new PingResponseSuccess();
            }
          }
        }

        return new PingResponsePending();
      })
    )
  );

  public sendResetOnPingSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType<PingResponseSuccess>(EDeviceHealthCheckActions.PingResponseSuccess),
      map(() => new SendReset())
    )
  );

  public sendReset$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SendReset>(EDeviceHealthCheckActions.SendReset),
      withLatestFrom(
        this._store.pipe(select(getVehicleWithEsnResponse)),
        this._store.pipe(select(getVehicleWithBoxResponse)),
        this._store.pipe(select(getPingRequestCorrelationId))
      ),
      switchMap(([, vehicleWithEsnResponse, vehicleWithBoxResponse, pingTransactionId]) =>
        this._deviceHealthCheckHttpService.sendReset(vehicleWithEsnResponse.esn, vehicleWithBoxResponse.boxId, pingTransactionId)
      ),
      map(response => (response?.transactionId ? new SendResetSuccess(response) : new SendResetFailure()))
    )
  );

  public sendResetSuccessStartPollingResponse$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SendResetSuccess>(EDeviceHealthCheckActions.SendResetSuccess),
      switchMap(() =>
        interval(this._pollingIntervalMs).pipe(
          takeUntil(
            this._actions$.pipe(
              ofType<ResetResponseSuccess | ResetResponseFailure | StopDeviceHealthCheckPolling>(
                EDeviceHealthCheckActions.ResetResponseSuccess,
                EDeviceHealthCheckActions.ResetResponseFailure,
                EDeviceHealthCheckActions.StopDeviceHealthCheckPolling
              )
            )
          )
        )
      ),
      withLatestFrom(this._store.pipe(select(getVehicleWithEsnResponse))),
      switchMap(([, vehicleWithEsnResponse]) =>
        this._deviceHealthCheckHttpService.getDeviceHealthCheckStatus(0, 1, vehicleWithEsnResponse.esn)
      ),
      map(response => {
        for (let i = 0; i <= response.results[0]?.length; i++) {
          const eventType = response.results[0][i]?.eventTypeInd;
          if (eventType === EDeviceEventTypeIndicator.ResetFailed) {
            return new ResetResponseFailure();
          }
          if (eventType === EDeviceEventTypeIndicator.ResetSuccess) {
            return new ResetResponseSuccess();
          }
        }

        return new ResetResponsePending();
      })
    )
  );

  public openDeviceHealthCheckQueryParams$ = createEffect(() =>
    this._activatedRoute.queryParamMap.pipe(
      filter(params => params.has(ROUTE_PARAMS.DeviceHealthCheck.DEVICE_HEALTH_CHECK_ID)),
      take(1),
      switchMap(params => {
        const itemId = parseInt(params.get(ROUTE_PARAMS.DeviceHealthCheck.DEVICE_HEALTH_CHECK_ID), 10);
        return [
          new GetVehicleSpecification(itemId),
          new GetVehicleWithEsn(itemId),
          new SelectRoutedVehicle(`v${itemId}`),
          new GetSelectedStatusItemId(itemId),
          new SelectVehicleIdForDeviceHealth(itemId.toString()),
          new OpenTroubleshootingPanel()
        ];
      })
    )
  );

  public triggerGetHistory$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVehicleWithEsnSuccess | ResetResponseSuccess | PingResponseFailure | ResetResponseFailure>(
        EDeviceHealthCheckActions.GetVehicleWithEsnSuccess,
        EDeviceHealthCheckActions.ResetResponseSuccess,
        EDeviceHealthCheckActions.PingResponseFailure,
        EDeviceHealthCheckActions.ResetResponseFailure
      ),
      map(() => new GetHistory())
    )
  );

  public getHistory$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetHistory>(EDeviceHealthCheckActions.GetHistory),
      withLatestFrom(this._store.pipe(select(getVehicleWithEsnResponse))),
      switchMap(([, vehicleWithEsnResponse]) =>
        this._deviceHealthCheckHttpService.getDeviceHealthCheckStatus(0, 2, vehicleWithEsnResponse.esn).pipe(
          map(
            response =>
              response?.results.map(result => ({
                pingStatus: this._getPingStatusFromBatch(result),
                resetStatus: this._getResetStatusFromBatch(result),
                requestedByUserId: result[0].userId,
                requestedDate: new Date(result[0].actionedAt),
                requestedByUsername: !!result[0].userDetails?.firstName
                  ? `${result[0].userDetails?.firstName} ${result[0].userDetails?.surname}`
                  : null
              }))
          )
        )
      ),
      map(response => (response?.length > 0 ? new GetHistorySuccess(response) : new GetHistorySuccess([])))
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _deviceHealthCheckHttpService: DeviceHealthCheckHttpService,
    private readonly _store: Store<IAppState>,
    private readonly _activatedRoute: ActivatedRoute
  ) {}

  private _getPingStatusFromBatch(healthCheckBatch: IDeviceHealthCheckDeviceEventResponse[]): EHealthCheckRequestState {
    for (let i = 0; i < healthCheckBatch.length; i++) {
      const eventType = healthCheckBatch[i].eventTypeInd;
      if (eventType === EDeviceEventTypeIndicator.PingFailed) {
        return EHealthCheckRequestState.Failure;
      }
      if (eventType === EDeviceEventTypeIndicator.PingSuccess) {
        return EHealthCheckRequestState.Success;
      }
    }
    return healthCheckBatch.find(x => x.eventTypeInd === EDeviceEventTypeIndicator.PingInProgress)
      ? EHealthCheckRequestState.Pending
      : EHealthCheckRequestState.NotSent;
  }

  private _getResetStatusFromBatch(healthCheckBatch: IDeviceHealthCheckDeviceEventResponse[]): EHealthCheckRequestState {
    for (let i = 0; i < healthCheckBatch.length; i++) {
      const eventType = healthCheckBatch[i].eventTypeInd;
      if (eventType === EDeviceEventTypeIndicator.ResetFailed) {
        return EHealthCheckRequestState.Failure;
      }
      if (eventType === EDeviceEventTypeIndicator.ResetSuccess) {
        return EHealthCheckRequestState.Success;
      }
    }
    return healthCheckBatch.find(x => x.eventTypeInd === EDeviceEventTypeIndicator.ResetInProgress)
      ? EHealthCheckRequestState.Pending
      : EHealthCheckRequestState.NotSent;
  }
}
